import FormatUtils from './utils/utils-format';
import RoutingUtils, { TopHitLocationDescriptorParams } from './utils/utils-routing';
import CommonUtils from './utils/utils-common';
import MetadataUtils from './utils/utils-metadata';
import { ProductFilter } from '../store/api';
import Keycloak from 'keycloak-js';
import { useKeycloak } from '../../useKeycloakAndInit';
import { useAuthentication } from '../app-store/authentication/useAuthentication';

/* Utils Facade */
export interface CatalogueLocationDescriptorParams {
    loanFormat: string | null | undefined;
    filter: ProductFilter;
    sortBy?: string | null | undefined;
    shopLanguage: string | undefined;
    isMultiLanguageShopEnabled: boolean;
    ageCategory: string | undefined;
}

interface SafeJoinComponentsParams {
    elements: any[];
    separator: string;
    tag?: string;
}

export default class Utils {
    static formatDuration(duration, options = {}) {
        return FormatUtils.formatDuration(duration, options);
    }

    static truncate(text, options = {}) {
        return FormatUtils.truncate(text, options);
    }

    static toTitleCase(str) {
        return FormatUtils.toTitleCase(str);
    }

    static buildUrl(...paths) {
        return RoutingUtils.buildUrl(paths);
    }

    static topHitLocationDescriptor({
        filter,
        scope,
        topHitLabel = '',
        shopLanguage,
        isMultiLanguageShopEnabled,
        ageCategory
    }: TopHitLocationDescriptorParams) {
        return RoutingUtils.topHitLocationDescriptor({
            filter,
            scope,
            topHitLabel,
            shopLanguage,
            isMultiLanguageShopEnabled,
            ageCategory
        });
    }

    static catalogueLocationDescriptorString({
        loanFormat,
        filter,
        sortBy = null,
        shopLanguage,
        isMultiLanguageShopEnabled,
        ageCategory
    }: CatalogueLocationDescriptorParams) {
        return RoutingUtils.catalogueLocationDescriptorString({
            loanFormat,
            filter,
            sortBy,
            shopLanguage,
            isMultiLanguageShopEnabled,
            ageCategory
        });
    }

    static joinComponents(elements, separator) {
        return CommonUtils.joinComponents(elements, separator);
    }

    static safeJoinComponents({ elements, separator, tag }: SafeJoinComponentsParams) {
        return CommonUtils.safeJoinComponents(elements, separator, tag);
    }

    static filterUntilTextLength<T>(
        elements: readonly T[],
        options: {
            maxLength?: number;
            getTextFunction: (element: T) => string;
            initialText?: string;
        }
    ): T[] {
        const { maxLength = 50, getTextFunction, initialText = '' } = options;

        let text = initialText;
        return elements.filter(element => {
            text += getTextFunction(element);
            return text.length <= maxLength;
        });
    }

    static withdrawnProductLine(product) {
        return MetadataUtils.withdrawnProductLine(product);
    }

    static isUserAdmin(keycloak: Keycloak) {
        return keycloak.hasRealmRole('connect_admin') || keycloak.hasRealmRole('connect_admin_demo');
    }
}

export function useIsAdmin() {
    const keycloak = useKeycloak();
    return Utils.isUserAdmin(keycloak);
}

export function useIsInPartnerContext() {
    const { spoc } = useAuthentication();
    return spoc.id?.includes('PARTNER') ?? false;
}

export function useIsUserAllowedToOrder() {
    const kc = useKeycloak();
    const { spoc } = useAuthentication();

    if (!spoc.id) {
        return false;
    }

    const match = spoc.id.match(/\D+/);
    if (!match) {
        return false;
    }

    const spocType = match[0].toLowerCase();
    return kc.hasRealmRole(`order_${spocType}`);
}

export function useIsCustomerAllowedToOrder() {
    const { spoc } = useAuthentication();
    return spoc.contentOrdersAllowed ?? false;
}
